import { createStore, type StoreApi } from "zustand/vanilla";
import { createJSONStorage, persist, type StateStorage } from "zustand/middleware";

/**
 * `P0-UIK-013`: the shell store, and the one rule of 4.6.5.
 *
 * **Danger sentence.** 4.6.5 states it once and calls it not a preference:
 * *"Per-dossier and per-persona work state lives in a single shell store and
 * never in a screen component. Changing tab, view or persona must never lose
 * work in progress."* The mockups learned that lesson repeatedly and wrote it
 * down twice , `hakili-conventions.md` lists *"état local qui devrait être
 * dans la coquille"* among the four recurring bug families , and the failure it
 * names is silent: a chef switches persona to check something, comes back, and
 * a half-written refusal motive is gone. Nothing throws. The user simply types
 * it again, or does not.
 *
 * So the store is a package rather than a per-app file. Three apps writing
 * this three times is three chances to put one field in a screen's `useState`,
 * and the field that gets forgotten is always the one somebody was in the
 * middle of.
 *
 * **The shape is canon's, not mine.** 4.6.5 gives it literally , `persona`,
 * `screenByPersona`, `work.byDossier` / `byVehicle` / `byPartner`,
 * `resetDossierWork`, `setWork` , and this file is that block made generic
 * over the persona union and the three work shapes, because a console `Vue`
 * and a bank persona are different unions of the same idea.
 *
 * **What it deliberately does NOT hold.** Server state (4.6.5's first row)
 * belongs to TanStack Query and is never copied here: a dossier's stage is the
 * server's answer, and a second copy is a second answer. This store holds only
 * what the user is in the middle of doing that the server does not yet own.
 */

/** An identifier as it arrives from the API. Opaque to the shell. */
export type Id = string;

/** The three keyed work spaces of 4.6.5, each app supplying its own shapes. */
export interface WorkShapes {
  readonly dossier: object;
  readonly vehicle: object;
  readonly partner: object;
}

export interface ShellWork<W extends WorkShapes> {
  readonly byDossier: Readonly<Record<Id, Partial<W["dossier"]>>>;
  readonly byVehicle: Readonly<Record<Id, Partial<W["vehicle"]>>>;
  readonly byPartner: Readonly<Record<Id, Partial<W["partner"]>>>;
}

export interface ShellState<Persona extends string, W extends WorkShapes> {
  readonly persona: Persona;
  /** 4.6.4: each persona keeps its OWN current screen, so switching loses none. */
  readonly screenByPersona: Readonly<Record<Persona, string>>;
  readonly work: ShellWork<W>;

  setPersona(persona: Persona): void;
  setScreen(persona: Persona, screen: string): void;

  /** The client's `resetDossierChoix`, generalised (4.6.5, and `hakili-conventions.md` §5). */
  resetDossierWork(dossierId: Id): void;
  setWork<K extends keyof W["dossier"]>(dossierId: Id, key: K, value: W["dossier"][K]): void;
  setVehicleWork<K extends keyof W["vehicle"]>(vehicleId: Id, key: K, value: W["vehicle"][K]): void;
  setPartnerWork<K extends keyof W["partner"]>(partnerId: Id, key: K, value: W["partner"][K]): void;
}

export interface ShellStoreOptions<Persona extends string> {
  /** Every persona the app can show. `screenByPersona` is seeded from it. */
  readonly personas: readonly Persona[];
  readonly initialPersona: Persona;
  /** The screen a persona lands on before it has visited one (4.6.4). */
  readonly landingScreen: (persona: Persona) => string;
  /**
   * 4.6.5: `sessionStorage` per tab, and `localStorage` for the client app,
   * *"because a client loses his connection mid-form and comes back on the
   * same handset"*. Passed in rather than chosen here: the rule is per app,
   * and a package that picked one would make the other app wrong.
   */
  readonly storage: StateStorage;
  /** The persistence key. Distinct per app, so two apps in one browser do not collide. */
  readonly name: string;
}

export type ShellStoreApi<Persona extends string, W extends WorkShapes> = StoreApi<ShellState<Persona, W>>;

/**
 * A keyed work space updated without mutating the one that was there.
 *
 * Written once because doing it by hand three times is how one of them ends up
 * mutating, and a mutated Zustand slice does not re-render , the failure looks
 * like *"my typing does not appear"*, which reads as a rendering bug.
 */
const putWork = <T extends object>(
  space: Readonly<Record<Id, Partial<T>>>,
  id: Id,
  key: keyof T,
  value: T[keyof T],
): Record<Id, Partial<T>> => ({
  ...space,
  // eslint-disable-next-line security/detect-object-injection -- `id` is an API identifier and the target is a plain record built here.
  [id]: { ...(space[id] ?? {}), [key]: value },
});

export function createShellStore<Persona extends string, W extends WorkShapes>(
  options: ShellStoreOptions<Persona>,
): ShellStoreApi<Persona, W> {
  const seeded = Object.fromEntries(
    options.personas.map((persona) => [persona, options.landingScreen(persona)]),
  ) as Record<Persona, string>;

  return createStore<ShellState<Persona, W>>()(
    persist(
      (set) => ({
        persona: options.initialPersona,
        screenByPersona: seeded,
        work: { byDossier: {}, byVehicle: {}, byPartner: {} },

        setPersona: (persona) => {
          // Note what does NOT happen here: no work is cleared and no screen is
          // reset. Switching persona is a change of view, not a change of task,
          // and 4.6.5's rule is precisely that it costs the user nothing.
          set({ persona });
        },
        setScreen: (persona, screen) => {
          set((state) => ({ screenByPersona: { ...state.screenByPersona, [persona]: screen } }));
        },

        resetDossierWork: (dossierId) => {
          set((state) => {
            // Rebuilt without the key rather than destructured-and-discarded:
            // the omit-by-rest idiom needs a binding nothing reads, which the
            // unused-variable rule refuses on sight and rightly so.
            const rest = Object.fromEntries(
              Object.entries(state.work.byDossier).filter(([id]) => id !== dossierId),
            );
            return { work: { ...state.work, byDossier: rest } };
          });
        },
        setWork: (dossierId, key, value) => {
          set((state) => ({
            work: { ...state.work, byDossier: putWork(state.work.byDossier, dossierId, key, value) },
          }));
        },
        setVehicleWork: (vehicleId, key, value) => {
          set((state) => ({
            work: { ...state.work, byVehicle: putWork(state.work.byVehicle, vehicleId, key, value) },
          }));
        },
        setPartnerWork: (partnerId, key, value) => {
          set((state) => ({
            work: { ...state.work, byPartner: putWork(state.work.byPartner, partnerId, key, value) },
          }));
        },
      }),
      {
        name: options.name,
        storage: createJSONStorage(() => options.storage),
        // The methods are not state and must not be written to storage: a
        // rehydrated store whose actions came from JSON has no actions at all.
        partialize: (state) => ({
          persona: state.persona,
          screenByPersona: state.screenByPersona,
          work: state.work,
        }),
      },
    ),
  );
}
